/**
 * LoadingScreen - Fetches sprint data and determines next phase
 * Loads members, orphan issues, and unestimated issues
 */

import { useEffect, useState } from 'react';
import { useGame } from '../context/GameContext';
import { ApiService } from '../services/ApiService';
import './LoadingScreen.css';

export function LoadingScreen() {
  const { state, actions } = useGame();
  const [loadingStep, setLoadingStep] = useState(0);
  const [loadingText, setLoadingText] = useState('Syncing with Jira...');

  useEffect(() => {
    if (!state.selectedSprint) {
      actions.setError('No sprint selected');
      actions.setPhase('BOOT');
      return;
    }

    // Flag to prevent state updates after unmount
    let isCancelled = false;

    const loadData = async () => {
      const sprintId = state.selectedSprint!.id;

      try {
        // Step 1: Load squad members
        setLoadingText('Loading squad members...');
        setLoadingStep(1);
        const members = await ApiService.getSquadMembers(sprintId);
        if (isCancelled) return;
        actions.setMembers(members);

        // Step 2: Load orphan issues
        setLoadingText('Checking for orphan issues...');
        setLoadingStep(2);
        const orphans = await ApiService.getOrphanIssues(sprintId);
        if (isCancelled) return;
        actions.setOrphanIssues(orphans);

        // Step 3: Load unestimated issues
        setLoadingText('Checking for unestimated issues...');
        setLoadingStep(3);
        const unestimated = await ApiService.getUnestimatedIssues(sprintId);
        if (isCancelled) return;
        actions.setUnestimatedIssues(unestimated);

        // Determine next phase
        setLoadingText('Ready!');
        setLoadingStep(4);

        // Short delay before transition
        await new Promise((resolve) => setTimeout(resolve, 500));
        if (isCancelled) return;

        // If there are orphan issues, go to ORPHAN_CHECK
        // Otherwise, go to game mode selection
        if (orphans.length > 0) {
          actions.setPhase('ORPHAN_CHECK');
        } else {
          actions.setPhase('GAME_MODE_SELECT');
        }
      } catch (error) {
        if (isCancelled) return;
        actions.setError(error instanceof Error ? error.message : 'Failed to load data');
        actions.setPhase('BOOT');
      }
    };

    loadData();

    // Cleanup: cancel pending operations on unmount
    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedSprint?.id]);

  return (
    <div className="screen loading-screen">
      <h2 className="title">LOADING</h2>

      <div className="loading-progress">
        <div className="loading-bar">
          <div
            className="loading-bar-fill"
            style={{ width: `${(loadingStep / 4) * 100}%` }}
          />
        </div>
      </div>

      <p className="loading-text">{loadingText}</p>

      <div className="loading-dots">
        <span className="loading-dot" style={{ animationDelay: '0ms' }}>.</span>
        <span className="loading-dot" style={{ animationDelay: '200ms' }}>.</span>
        <span className="loading-dot" style={{ animationDelay: '400ms' }}>.</span>
      </div>

      <div className="loading-info">
        <p>Sprint: {state.selectedSprint?.name}</p>
        <p>Board: {state.selectedBoard?.name}</p>
      </div>
    </div>
  );
}
